import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { intervalSocketBunja } from '@flipster/r230522/original/interval/socket';
import { Styles, css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import NearWorldClock from '@flipster/utils/clock/near-world-clock';
import { tradeLogger } from '@flipster/utils/datadog';
import { map } from '@flipster/utils/map';

import { waitBarsUpdater } from './bars';
import { CustomHeader } from './custom-header';
import { resolutionToSecs, sanitizeSymbolName } from './misc/helper';
import type { ChartLoadRecorder } from './observability/chart-load-recorder';
import { datafeed } from './options/datafeed';
import TradingView from './trading-view';
import { TvLoading } from './tv-loading';

interface Props {
  tv: TradingView | undefined;
  customHeader: ReactNode;
  fullscreen?: boolean;
  css?: Styles;
  chartLoadRecorder?: ChartLoadRecorder;
}

/**
 * @description
 * The latest bar doesn't arrive immediately when the minutes change
 * Mark the threshold as 10%
 * ex) 2025-05-21T11:30:00Z bar arrives in 2025-05-21T11:30:01Z
 */
const latestBarThreshold = 1.1;

export const TvLayout = ({ tv, customHeader, fullscreen, css: cssProps, chartLoadRecorder }: Props) => {
  const currentSymbolRef = useRef<string>('dummy');
  const { r230522Socket, isConnectedIntervalAtom } = useBunja(intervalSocketBunja);
  const [currentResolution, setCurrentResolution] = useState<number>();
  const [currentSymbol, setCurrentSymbol] = useState<string>();
  const [isReady, setReady] = useState<boolean>(false);
  const isConnected = useAtomValue(isConnectedIntervalAtom);
  const [isLatestBarLoaded, setIsLatestBarLoaded] = useState(false);

  const barsUpdaterPromise = useMemo(() => {
    if (!currentResolution || !currentSymbol) return null;
    return waitBarsUpdater(sanitizeSymbolName(currentSymbol), currentResolution);
  }, [currentResolution, currentSymbol]);

  const updateLatestBarLoaded = useCallback(async () => {
    const barsUpdater = await barsUpdaterPromise;
    if (!barsUpdater) {
      setIsLatestBarLoaded(false);
      return;
    }
    const isFresh = barsUpdater.remainingBarFromNow <= latestBarThreshold;
    setIsLatestBarLoaded(isFresh);
    if (isFresh) chartLoadRecorder?.mark('realtime_fresh');
  }, [barsUpdaterPromise, chartLoadRecorder]);

  useEffect(
    () =>
      r230522Socket.on('closed', () => {
        chartLoadRecorder?.beginAttempt('reconnect', {
          symbol: currentSymbol,
          resolution: currentResolution?.toString(),
        });
        setIsLatestBarLoaded(false);
      }),
    [chartLoadRecorder, currentResolution, currentSymbol, r230522Socket],
  );
  useEffect(() => r230522Socket.on('connected', updateLatestBarLoaded), [r230522Socket, updateLatestBarLoaded]);
  useEffect(() => {
    const offTickPromise = barsUpdaterPromise?.then((barsUpdater) => {
      updateLatestBarLoaded();
      // NOTE: If the effect occurs raise condition, tick event never subscribed
      return barsUpdater.on('tick', () => updateLatestBarLoaded());
    });
    return () => {
      offTickPromise?.then((offTick) => offTick());
    };
  }, [barsUpdaterPromise, updateLatestBarLoaded]);

  useEffect(() => tv?.on('onFirstDataLoaded', updateLatestBarLoaded), [tv, updateLatestBarLoaded]);
  useEffect(() => tv?.on('onIntervalChanged', (interval) => setCurrentResolution(resolutionToSecs(interval))), [tv]);
  useEffect(
    () =>
      tv?.on('onSymbolChanged', (symbol) => {
        setCurrentSymbol(symbol);
        setIsLatestBarLoaded(false);
      }),
    [tv],
  );

  useEffect(() => {
    if (!tv) return;
    const handler = (ready: boolean) => {
      setReady(ready);
      setCurrentSymbol(tv.symbol);
      setCurrentResolution(map(tv.interval, resolutionToSecs));
    };
    if (tv.enabled) {
      handler(true);
      return;
    }
    return tv.on('isReady', handler);
  }, [tv]);

  const errorLogSent = useRef(false);
  useEffect(() => {
    if (isReady && isLatestBarLoaded) return;
    const id = setTimeout(() => {
      if (errorLogSent.current) return;
      errorLogSent.current = true;
      if (!currentResolution || !currentSymbol) {
        tradeLogger.error('chart_not_loaded', {
          ...chartLoadRecorder?.markDelayed(),
          error_message: 'Resolution or symbol not resolved',
          error_category: 'javascript_errors',
          symbol: currentSymbol,
          timeframe: currentResolution,
          trading_view_loaded: isReady,
          latest_bar_loaded: isLatestBarLoaded,
          websocket_connected: isConnected,
          url: window.location.href,
          ntp_time_gap: NearWorldClock.gapMilliSecondsFromSystemTime,
        });
      } else {
        barsUpdaterPromise?.then((barsUpdater) => {
          tradeLogger.error('chart_not_loaded', {
            ...chartLoadRecorder?.markDelayed(),
            error_message: 'Latest bar is not loaded',
            error_category: 'javascript_errors',
            symbol: currentSymbol,
            timeframe: currentResolution,
            gap: barsUpdater.remainingBarFromNow,
            trading_view_loaded: isReady,
            latest_bar_loaded: isLatestBarLoaded,
            websocket_connected: isConnected,
            url: window.location.href,
            ntp_time_gap: NearWorldClock.gapMilliSecondsFromSystemTime,
          });
        });
      }
    }, 10000);
    return () => clearTimeout(id);
  }, [
    barsUpdaterPromise,
    chartLoadRecorder,
    currentResolution,
    currentSymbol,
    isLatestBarLoaded,
    isReady,
    isConnected,
  ]);

  useEffect(() => {
    return datafeed.on('onNoData', ({ symbol, period }) => {
      const symbolsShouldLog = ['BTCUSDT', 'ETHUSDT', 'BCHUSDT', 'XRPUSDT'];

      if (
        symbolsShouldLog.some(
          (symbolShouldLog) => symbol.startsWith(symbolShouldLog) /* symbol is like <BTCUSDT(.PERP)?>:<MID|MARK> */,
        )
      ) {
        tradeLogger.error('chart_data_missing', {
          error_message: 'First loaded data is empty',
          error_category: 'data_errors',
          symbol,
          timeframe: currentResolution,
          period_from: period.from,
          period_to: period.to,
          trading_view_loaded: isReady,
          websocket_connected: isConnected,
          url: window.location.href,
        });
      }
    });
  }, [currentResolution, isReady, isConnected]);

  const isLoading = !isReady || !isLatestBarLoaded;

  useEffect(() => {
    if (isLoading) return;
    chartLoadRecorder?.mark('overlay_hidden');
    chartLoadRecorder?.succeed();
  }, [chartLoadRecorder, isLoading]);

  if (currentSymbol) currentSymbolRef.current = currentSymbol;
  useEffect(() => {
    if (!isLoading) return;
    const id = setTimeout(() => {
      tradeLogger.info('chart_loading_delayed', {
        ...chartLoadRecorder?.markDelayed(),
        symbol: currentSymbolRef.current,
        visibilityState: document.visibilityState,
      });
    }, 10000);
    return () => {
      clearTimeout(id);
    };
  }, [chartLoadRecorder, isLoading]);

  return (
    // dropdown 을 위한 id
    <section
      id='tradingview'
      className={css(
        stack.raw({
          fontFamily: '-apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif',
          pos: 'relative',
          h: fullscreen ? '100dvh' : '100%',
        }),
        cssProps,
      )}
    >
      {tv && <CustomHeader tv={tv}>{customHeader}</CustomHeader>}
      <div id={TradingView.container} className={css({ flex: 1 })} />
      {isLoading && <TvLoading />}
    </section>
  );
};
